import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { DataTablePagination } from "../../../../components/ui/table-pagination";
import { cn } from "../../../../lib/utils";
import {
  type AdministrativeAreaType,
  type SetAdministrativeAreaTypesModalOptions,
} from "../../types";
import { ActionsCell } from "./components/ActionsCell";
import { ActiveBadge } from "./components/ActiveBadge";
import { AdministrativeAreaTypesDataTable } from "./components/AdministrativeAreaTypesDataTable";
import { AuditCell } from "./components/AuditCell";
import { PlaceholderValue } from "./components/PlaceholderValue";
import { SortableColumnHeader } from "./components/SortableColumnHeader";
import { COMPACT_HEADER_CLASS, DEFAULT_PAGE_SIZE } from "./constants";
import { type AdministrativeAreaTypeColumnDef } from "./types";
import { languages } from "../../../../utils/i18n";
import {
  createParentLookup,
  getName,
  getParentName,
  getRowId,
  getTimestamp,
  getTranslatedName,
} from "./utils";

interface ListProps {
  administrativeAreaTypes?: AdministrativeAreaType[];
  errorMessage?: string;
  isFetching?: boolean;
  pageSize?: number;
  setModalOptions: SetAdministrativeAreaTypesModalOptions;
}

const createColumns = ({
  languageCode,
  languageLabel,
  onOpenModal,
  parentLookup,
  t,
}: {
  languageCode: string;
  languageLabel: string;
  onOpenModal: (areaType: AdministrativeAreaType, type: "edit" | "view") => void;
  parentLookup: Map<string, AdministrativeAreaType>;
  t: TFunction;
}): AdministrativeAreaTypeColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.view")} / {t("common.edit")}
      </span>
    ),
    cell: ({ row }) => <ActionsCell areaType={row.original} onOpenModal={onOpenModal} t={t} />,
    enableSorting: false,
    meta: {
      cellClassName: "w-0 px-3 align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "name",
    accessorFn: getName,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.name")} />
    ),
    cell: ({ row }) => getName(row.original) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName:
        "min-w-48 max-w-64 whitespace-normal px-3 align-top font-medium text-foreground",
      headerClassName: cn("min-w-48 max-w-64", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "translation",
    accessorFn: row => getTranslatedName(row, languageCode),
    header: () => t("commodities.list.column.translation", { language: languageLabel }),
    cell: ({ row }) => (
      <span className="block whitespace-normal leading-tight text-foreground">
        {getTranslatedName(row.original, languageCode)}
      </span>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "min-w-56 max-w-80 px-3 align-top",
      headerClassName: cn("min-w-56 max-w-80", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "parent",
    accessorFn: row => getParentName(row, parentLookup, t),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("administrativeAreaTypes.parent")} />
    ),
    cell: ({ row }) => (
      <span className="block whitespace-normal leading-tight">
        {getParentName(row.original, parentLookup, t)}
      </span>
    ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-56 px-3 align-top",
      headerClassName: cn("min-w-32 max-w-56", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "lastUpdated",
    accessorFn: row => getTimestamp(row.last_update_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "created",
    accessorFn: row => getTimestamp(row.creation_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "active",
    accessorFn: row => Boolean(row.active),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.active")} />
    ),
    cell: ({ row }) => <ActiveBadge isActive={row.original.active} t={t} />,
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-24 px-3 align-top",
      headerClassName: cn("min-w-24", COMPACT_HEADER_CLASS),
    },
  },
];

export const List = ({
  administrativeAreaTypes = [],
  errorMessage = "",
  isFetching = false,
  pageSize = DEFAULT_PAGE_SIZE,
  setModalOptions,
}: ListProps) => {
  const { i18n, t } = useTranslation();
  const resolvedPageSize = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  const hasAdministrativeAreaTypes = administrativeAreaTypes.length > 0;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: resolvedPageSize,
  });
  const parentLookup = useMemo(
    () => createParentLookup(administrativeAreaTypes),
    [administrativeAreaTypes]
  );
  const languageLabel = useMemo(
    () =>
      (languages as { label: string; value: string }[]).find(
        language => language.value === i18n.language
      )?.label ?? i18n.language,
    [i18n.language]
  );
  const columns = useMemo(
    () =>
      createColumns({
        languageCode: i18n.language,
        languageLabel,
        onOpenModal: (areaType, type) =>
          setModalOptions({ id: areaType.id ?? undefined, isOpen: true, type }),
        parentLookup,
        t,
      }),
    [i18n.language, languageLabel, parentLookup, setModalOptions, t]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageSize === resolvedPageSize
        ? currentPagination
        : { pageIndex: 0, pageSize: resolvedPageSize }
    );
  }, [resolvedPageSize]);

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [sorting]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(administrativeAreaTypes.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [administrativeAreaTypes.length]);

  const table = useReactTable({
    columns,
    data: administrativeAreaTypes,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <AdministrativeAreaTypesDataTable
        emptyMessage={t("administrativeAreaTypes.empty", {
          defaultValue: "No administrative area types found",
        })}
        isLoading={isFetching}
        table={table}
      />
      {hasAdministrativeAreaTypes ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
